// The scratch sketch layer for `hints.scratch` (contracts/launch-types.md §1, board 16 L1 Zip Path): a
// pointer drag draws an accent path over the board; on a grid it snaps to the squares it passes (going
// back over the previous square takes a step back). Keyboard: focus the pad, arrow keys draw from square to
// square, Backspace takes a step back, Escape clears. Reset clears it. It is local state: never part of
// the selection, never submitted.
import { useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import styles from "./launch.module.css";

interface Point {
  /** Fractions of the layer's width and height. */
  x: number;
  y: number;
  /** "r,c" when snapped to a grid square. */
  cell?: string;
}

const CAPTION = "Drag on the grid to sketch · only your answer counts";

/**
 * Wraps a board. `enabled`: the spec asks for scratch (and the board is being played); `blocked`: the
 * board takes taps itself, so the sketch pad can't sit on top of it (the caption still shows in previews).
 */
export function ScratchLayer({ enabled, preview, children }: { enabled: boolean; preview: boolean; children: ReactNode }) {
  if (enabled) return <ScratchPad>{children}</ScratchPad>;
  if (!preview) return <>{children}</>;
  return (
    <div className={styles.scratchWrap}>
      {children}
      <p className={styles.scratchCaption}>{CAPTION}</p>
    </div>
  );
}

function ScratchPad({ children }: { children: ReactNode }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [strokes, setStrokes] = useState<Point[][]>([]);
  /** A grid square's width in px, for the translucent band under the line. */
  const [cellPx, setCellPx] = useState(0);
  const drawing = useRef(false);
  const points = strokes.flat();
  const hasSketch = points.length > 0;

  const frame = () => wrapRef.current?.getBoundingClientRect() ?? null;

  /** The grid square under a screen point, if the board is a grid: its key and centre (as fractions). */
  const squareAt = (clientX: number, clientY: number): Point | null => {
    const rect = frame();
    if (!rect || typeof document.elementsFromPoint !== "function") return null;
    const cell = document
      .elementsFromPoint(clientX, clientY)
      .find((el): el is HTMLElement => el instanceof HTMLElement && el.dataset.cell !== undefined && wrapRef.current!.contains(el));
    if (!cell) return null;
    const box = cell.getBoundingClientRect();
    if (box.width && Math.abs(box.width - cellPx) > 0.5) setCellPx(box.width);
    return { x: (box.left + box.width / 2 - rect.left) / rect.width, y: (box.top + box.height / 2 - rect.top) / rect.height, cell: cell.dataset.cell };
  };

  const extend = (p: Point, fresh: boolean) =>
    setStrokes((all) => {
      if (fresh || all.length === 0) return [...all, [p]];
      const stroke = all[all.length - 1]!;
      const last = stroke[stroke.length - 1];
      if (p.cell !== undefined) {
        if (last?.cell === p.cell) return all;
        // Back over the previous square: a step back, as when tracing a path on the board.
        if (stroke.length >= 2 && stroke[stroke.length - 2]!.cell === p.cell) return [...all.slice(0, -1), stroke.slice(0, -1)];
      } else if (last && Math.hypot(p.x - last.x, p.y - last.y) < 0.006) return all;
      return [...all.slice(0, -1), [...stroke, p]];
    });

  const pointAt = (e: PointerEvent) => {
    const snapped = squareAt(e.clientX, e.clientY);
    if (snapped) return snapped;
    const rect = frame();
    if (!rect || !rect.width || !rect.height) return null;
    return { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const p = pointAt(e);
    if (!p) return;
    drawing.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    extend(p, true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drawing.current) return;
    const p = pointAt(e);
    if (p) extend(p, false);
  };
  const end = () => {
    drawing.current = false;
  };

  /** Keyboard drawing: arrow keys move from square to square (on a grid). */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
    if (e.key === "Escape") {
      if (hasSketch) e.preventDefault();
      setStrokes([]);
      return;
    }
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      setStrokes((all) => {
        const stroke = all[all.length - 1];
        if (!stroke) return all;
        return stroke.length > 1 ? [...all.slice(0, -1), stroke.slice(0, -1)] : all.slice(0, -1);
      });
      return;
    }
    const d = delta[e.key];
    if (!d) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    e.preventDefault();
    const last = points[points.length - 1]?.cell;
    const cells = [...wrap.querySelectorAll<HTMLElement>("[data-cell]")];
    const target = last
      ? (() => {
          const [r, c] = last.split(",").map(Number) as [number, number];
          return wrap.querySelector<HTMLElement>(`[data-cell="${r + d[0]},${c + d[1]}"]`);
        })()
      : // The first press starts the sketch on checkpoint "1" when the board has one (Zip Path), else the first square.
        (cells.find((el) => el.textContent?.trim() === "1") ?? cells[0]);
    if (!target) return;
    const rect = wrap.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      // Not laid out (tests, print): still record the step.
      extend({ x: 0, y: 0, cell: target.dataset.cell }, !last);
      return;
    }
    if (box.width && Math.abs(box.width - cellPx) > 0.5) setCellPx(box.width);
    extend({ x: (box.left + box.width / 2 - rect.left) / rect.width, y: (box.top + box.height / 2 - rect.top) / rect.height, cell: target.dataset.cell }, !last);
  };

  const squares = points.filter((p) => p.cell).length;
  const status = hasSketch ? (squares ? `Sketch: ${squares} squares` : "Sketch drawn") : "No sketch";
  const band = cellPx * 0.62;
  return (
    <div className={styles.scratchWrap}>
      <div className={styles.scratchStage} ref={wrapRef}>
        {children}
        <svg className={styles.scratchSvg} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden data-scratch={points.length}>
          {strokes.map((stroke, i) => {
            const d = stroke.map((p, k) => `${k ? "L" : "M"}${(p.x * 100).toFixed(2)},${(p.y * 100).toFixed(2)}`).join("");
            return (
              <g key={i}>
                {band && stroke.some((p) => p.cell) ? (
                  <path d={d} className={styles.scratchBand} style={{ strokeWidth: `${band}px` }} vectorEffect="non-scaling-stroke" />
                ) : null}
                <path d={d} className={styles.scratchLine} vectorEffect="non-scaling-stroke" />
              </g>
            );
          })}
        </svg>
        <div
          className={styles.scratchPad}
          role="group"
          tabIndex={0}
          aria-label="Scratch pad. Drag on the board, or use the arrow keys to sketch from square to square, starting at 1. Backspace steps back, Escape clears. Only your answer counts."
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={end}
          onPointerCancel={end}
          onKeyDown={onKeyDown}
          data-drawing={drawing.current || undefined}
        />
      </div>
      <div className={styles.scratchBar}>
        <p className={styles.scratchCaption}>{CAPTION}</p>
        <span className={styles.srOnly} aria-live="polite">
          {status}
        </span>
        {hasSketch ? (
          <button type="button" className={styles.textButton} onClick={() => setStrokes([])}>
            Reset sketch
          </button>
        ) : null}
      </div>
    </div>
  );
}
